/**
 * ImpersonationLogTab — journal permanent « Connexions en tant que client » (Sécurité › En tant que) — Batch #14.338.
 */
import { useEffect, useState } from "react";
import { Eye, FlaskConical, RefreshCw, ShieldAlert } from "lucide-react";
import { api } from "@/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");
const dur = (a, b) => {
  if (!a) return "—";
  const ms = (b ? new Date(b) : new Date()) - new Date(a);
  const m = Math.max(0, Math.round(ms / 60000));
  return m < 1 ? "< 1 min" : `${m} min`;
};

export default function ImpersonationLogTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    api.get("/admin/impersonation/log", { params: { limit: 200 } }).then((r) => setData(r.data)).catch(() => setData({ items: [] })).finally(() => setLoading(false));
  };
  useEffect(load, []);
  const items = data?.items || [];
  return (
    <div className="space-y-3" data-testid="impersonation-log-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-600" /> Connexions « en tant que client »</h3>
          <p className="text-xs text-gray-500">Chaque ouverture du portail d'un client par l'administrateur est inscrite ici de façon permanente (qui, quel client, motif, mode, durée, actions bloquées).</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          {data && <span data-testid="impersonation-log-count">{items.length} session(s) · {data.active || 0} active(s)</span>}
          <button onClick={load} className="border rounded-lg p-1.5 text-gray-400 hover:text-gray-600" data-testid="impersonation-log-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border rounded-xl p-6 text-center text-sm text-gray-400" data-testid="impersonation-log-empty">Aucune navigation « en tant que » enregistrée.</div>
      ) : (
        <div className="bg-white border rounded-xl overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">Début</th><th className="text-left px-3 py-2">Administrateur</th><th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Motif</th><th className="text-left px-3 py-2">Mode</th><th className="text-left px-3 py-2">Durée</th>
                <th className="text-left px-3 py-2">Fin</th><th className="text-right px-3 py-2">Bloquées</th><th className="text-left px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((r) => (
                <tr key={r.id} className={r.active ? "bg-red-50/50" : ""} data-testid={`impersonation-log-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(r.started_at)}</td>
                  <td className="px-3 py-2">{r.admin_email}</td>
                  <td className="px-3 py-2"><div className="font-medium text-slate-800">{r.client_name}</div>{r.portal_username && <div className="text-[10px] text-slate-400 font-mono">@{r.portal_username}</div>}</td>
                  <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={r.reason || ""}>{r.reason || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2">
                    {r.mode === "test"
                      ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"><FlaskConical className="w-3 h-3" /> test</span>
                      : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"><Eye className="w-3 h-3" /> lecture seule</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{dur(r.started_at, r.ended_at || (r.active ? null : r.expires_at))}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.active ? <span className="text-red-700 font-semibold">en cours</span> : r.ended_at ? `${fmt(r.ended_at)} (${r.ended_by || "—"})` : <span className="text-slate-500">expirée</span>}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.blocked_count || 0}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{r.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
